<template>
    <div
        :id="id"
        data-bs-keyboard-="false"
        class="modal fade bd-example-modal-xl"
        role="dialog"
        aria-labelledby="myExtraLargeModalLabel"
        aria-hidden="true"
    >
        <div role="document" class="modal-dialog" style="max-width: 900px">
            <div class="modal-content container">
                <div class="modal-header">
                    <h2 class="modal-title" id="myExtraLargeModalLabel">
                        {{ title }}
                    </h2>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body">
                    <form @submit.prevent="upload">
                        <div class="mb-3">
                            <label for="fileUpload" class="form-label">{{ $t("Select files") }}:</label>
                            <AppFileSelect v-model:files="files" />
                        </div>
                        <div class="d-flex align-items-center gap-3">
                            <button type="submit" class="btn btn-primary" @submit="upload">{{ $t("Upload") }}</button>
                            <br />
                            <div v-if="loading" class="progress-container ms-3" style="flex-grow: 1">
                                <div
                                    class="progress-step"
                                    v-for="step in totalSteps"
                                    :key="step"
                                    :class="{ active: step <= currentStep }"
                                ></div>
                                <div class="progress-percentage">{{ uploadProgress }}%</div>
                            </div>
                            <div v-if="showSuccessMessage" class="text-success mt-3">
                                {{ $t("File uploaded successfully!") }}
                            </div>
                        </div>
                        <div :class="{ invisible: !showError, visible: showError }" class="text-danger mt-3">
                            {{ $t("Upload failed, please try again in a few moments.") }}
                            <div class="mt-2">{{ validationErrors }}</div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import AppFileSelect from "./AppFileSelect.vue";
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { trans as $t } from "@/vue/composables/missingTranslations";

const props = defineProps({
    id: { type: String, default: "" },
    title: { type: String, default: "" },
    url: { type: String, default: "" }
});

const emits = defineEmits(["update:response"]);

const loading = ref(false);
const totalSteps = 20;
const uploadProgress = ref(0);
const currentStep = computed(() => Math.ceil((uploadProgress.value / 100) * totalSteps));
const showSuccessMessage = ref(false);
const showError = ref(false);
const selectedOrganizationValid = ref(true);
const validationMessage = ref("");
const validationErrors = ref("");
const files = ref([]);

function upload() {
    validationErrors.value = "";
    showError.value = false;
    showSuccessMessage.value = false;
    selectedOrganizationValid.value = true;
    validationMessage.value = "";

    if (files.value.length) {
        const formData = new FormData();
        files.value.forEach((_file) => {
            formData.append("sd_uploads[]", _file);
        });

        const config: AxiosRequestConfig = {
            headers: {
                "Content-Type": "multipart/form-data"
            },
            onUploadProgress: function ({ total, loaded }) {
                if (total) {
                    uploadProgress.value = Math.round((loaded * 100) / total);
                }
            }
        };

        loading.value = true;
        axios
            .post(props.url, formData, config)
            .then((response) => {
                showSuccessMessage.value = true;
                showError.value = false;
                emits("update:response", response);
            })
            .catch((error: AxiosError) => {
                const res = error?.response as AxiosResponse;
                if (res && res.status === 422 && res?.data?.message) {
                    validationErrors.value = res.data.message;
                }

                showError.value = true;
                showSuccessMessage.value = false;
            })
            .finally(() => {
                loading.value = false;
                uploadProgress.value = 0;
            });
    }
}
</script>
<style>
.progress-container {
    display: flex;
    justify-content: center;
    margin-left: 15px;
    align-items: center;
    background-color: #f5f5f5;
    padding: 10px;
    border-radius: 5px;
    box-shadow: inset 0 0 5px rgba(0, 0, 0, 0.2);
    overflow: hidden;
}

.progress-step {
    height: 20px;
    width: 5%;
    margin: 0 2px;
    background-color: #d9d9d9;
    transition: background-color 0.3s ease;
    border-radius: 5px;
}

.progress-step.active {
    background-color: #4caf50;
}
.progress-percentage {
    position: absolute;
    width: 100%;
    text-align: center;
    top: 50%;
    transform: translateY(-50%);
    font-weight: bold;
    color: #fff;
}
</style>
